
/**
 * Module dependencies.
 */

import { Controller, ErrorOption } from 'react-hook-form';
import {
  DropzoneOptions,
  FileError,
  FileRejection,
  useDropzone
} from 'react-dropzone';

import {
  FormControl,
  FormGroup,
  HelpText,
  Label
} from 'src/components/core/forms/fields-styled-components';

import { color, units } from 'src/styles/utils';
import { getKeyFromObject } from 'src/core/utils/objects';
import { ifProp, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import React, { ReactElement, useCallback } from 'react';
import Svg from 'src/components/core/svg';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import styled, { css } from 'styled-components';
import uploadIcon from 'src/assets/svg/upload.svg';

/**
 * Error codes.
 */

const errorCodes = {
  fileTooLarge: 'file-too-large',
  fileTooSmall: 'file-too-small'
};

/**
 * `DropzoneProps` type.
 */

type DropzoneProps = Pick<DropzoneOptions, 'accept' | 'minSize' | 'maxSize' | 'maxFiles'| 'onDrop'>

/**
 * `Error` type.
 */

type Error = {
  message: string,
  type: string
};

/**
 * `Props` type.
 */

type Props = DropzoneProps & {
  as?: any,
  className?: string,
  clearError: (name: string) => void,
  clearValue: (name: string) => void,
  control: any,
  disabled?: boolean,
  error?: Error | null | undefined,
  helpText?: string,
  id?: string,
  label: string,
  name: string,
  placeholder?: string,
  rules?: {
    [key: string]: any
  },
  setError: (name: string, error: ErrorOption) => void,
  setValue: (name: string, value: any) => void,
  type?: string
};

/**
 * `FormControlWrapper` styled component.
 */

const FormControlWrapper = styled(FormControl).attrs({ as: 'div' })`
  align-items: center;
  display: grid;
  grid-gap: ${units(1)};
  grid-template-areas:
    'value icon  .'
    'input input input';
  grid-template-columns: 1fr max-content 0;
  grid-template-rows: repeat(2, auto);
  position: relative;
`;

/**
 * `StyledInput` styled component.
 */

const StyledInput = styled.input`
  grid-area: input;
  grid-row: 1 / -1;
  opacity: 0;
`;

/**
 * `Value` styled component.
 */

const Value = styled.div`
  grid-area: value;
  grid-row: 1 / -1;
  overflow: hidden;
  pointer-events: none;
  text-overflow: ellipsis;
  transition: color ${theme('animations.defaultTransition')};
  white-space: nowrap;
`;

/**
 * `Placeholder` styled component.
 */

const Placeholder = styled.span<{ hasError: boolean }>`
  color: ${color('grey400')};

  ${ifProp('hasError', css`
    color: ${color('formError')};
  `)}
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg).attrs({
  icon: uploadIcon,
  size: units(4)
})`
  grid-area: icon;
  grid-row: 1 / -1;
`;

/**
 * `Upload` component.
 */

const Upload = (props: Props): ReactElement => {
  const {
    accept,
    className,
    clearError,
    clearValue,
    disabled,
    error,
    helpText,
    id,
    label,
    maxFiles,
    maxSize,
    minSize,
    name,
    onDrop,
    placeholder,
    setError,
    setValue,
    ...rest
  } = props;

  const { translate } = useTranslate();
  const handleDropAccepted = useCallback(file => {
    clearError(name);
    setValue(name, file[0]);
  }, [clearError, name, setValue]);

  const handleDropRejected = useCallback((files: FileRejection[]) => {
    const { code, message }: FileError = head(get(head(files), 'errors'));

    clearValue(name);

    if (!code || !Object.values(errorCodes).includes(code)) {
      return {
        message,
        type: 'manual'
      };
    }

    const translatedKey = getKeyFromObject(errorCodes, code);

    setError(name, {
      message: translate(`common:upload.errors.${translatedKey}`, {
        maxSize: maxSize / 1000000,
        minSize
      }),
      type: 'manual'
    });
  }, [clearValue, maxSize, minSize, name, setError, translate]);

  const { acceptedFiles, getInputProps, getRootProps } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onDrop,
    onDropAccepted: handleDropAccepted,
    onDropRejected: handleDropRejected
  });

  const hasError = !isEmpty(error);
  const value = get(acceptedFiles[0], 'path');

  return (
    <FormGroup
      className={className}
      disabled={disabled}
    >
      <Label>
        {label}
      </Label>

      <FormControlWrapper
        aria-label={label}
        disabled={disabled}
        hasError={hasError}
        {...getRootProps()}
      >
        <Controller
          disabled={disabled}
          id={id ?? name}
          name={name}
          render={({ onBlur, onChange }) => (
            <StyledInput {...getInputProps({ onBlur, onChange })} />
          )}
          {...rest}
          multiple={false}
          style={{ display: 'initial' }}
        />

        <Value>
          {value ? value : (
            <Placeholder hasError={hasError}>
              {placeholder}
            </Placeholder>
          )}
        </Value>

        <Icon />
      </FormControlWrapper>

      {hasError && error?.message && (
        <HelpText hasError>
          {error?.message}
        </HelpText>
      )}

      {helpText && !hasError && (
        <HelpText>
          {helpText}
        </HelpText>
      )}
    </FormGroup>
  );
};

/**
 * Export `Upload` component.
 */

export default Upload;
